import {useCallback, useState} from "react";

interface DialogState<T> {
	open: boolean;
	key: number;
	value: T | null;
}

/**
 * Open/close state for a modal whose content remounts fresh on every open.
 *
 * `Modal.Root` must stay outside the keyed child, and `key` advances on {@link show} only —
 * remounting on close would replace the still-open dialog and skip its exit transition.
 * `value` carries the identity the dialog was opened for (e.g. the row) and survives
 * closing, so the dialog keeps its texts while it fades out.
 */
export function useDialogState<T = void>() {
	const [state, setState] = useState<DialogState<T>>({open: false, key: 0, value: null});

	const show = useCallback((value: T) => {
		setState((previous) => ({open: true, key: previous.key + 1, value}));
	}, []);

	const close = useCallback(() => {
		setState((previous) => ({...previous, open: false}));
	}, []);

	/* replaces the value of a dialog that stays open — {@link show} would remount its content */
	const update = useCallback((value: T) => {
		setState((previous) => ({...previous, value}));
	}, []);

	/* for Modal.Root, which only ever closes this way — opening must go through show() */
	const onOpenChange = useCallback(
		(open: boolean) => {
			if (!open) close();
		},
		[close],
	);

	return {...state, show, close, update, onOpenChange};
}
